/**
 * The Bar menu: what is written on a bar rather than on a note.
 *
 * Barlines, repeats and their endings, the signs that jump from one bar to another, where a
 * system or a page breaks, and how long a bar lasts when it is an upbeat. Everything here writes to the bar the caret stands in and is
 * greyed out rather than hidden when it stands in none.
 */

import { bars, measureTicks, plain, resolveAttributes, ticksOf, withDots } from "@app/score-editor";
import type { BarlineStyle, MeasureId, Score } from "@app/score-editor";
import type { ScoreActions } from "./score-actions";
import { RULE } from "./score-menu-model";
import type { Menu } from "./score-menu-model";
import { DURATIONS } from "./score-menus-music";

export const BARLINES: readonly { style: BarlineStyle; label: string }[] = [
  { style: "regular", label: "Regular" },
  { style: "double", label: "Double" },
  { style: "final", label: "Final" },
  { style: "dashed", label: "Dashed" },
  { style: "heavy", label: "Heavy" },
  { style: "none", label: "None" },
];

/**
 * The two signs a jump goes to and the two instructions that are read on the way back.
 *
 * Whether the words come out as `al Fine` or as `al Coda` is not asked, because it is not
 * a choice: it follows from which of these the piece carries. See `JumpMarks`.
 */
export interface Sign {
  readonly key: "segno" | "coda" | "toCoda" | "fine";
  /** What the menu says, which has room for a sentence. */
  readonly label: string;
  /** What the inspector's button says, which has room for a word. */
  readonly short: string;
}

export const SIGNS: readonly Sign[] = [
  { key: "segno", label: "Segno on this bar", short: "Segno" },
  { key: "coda", label: "Coda starts at this bar", short: "Coda" },
  { key: "toCoda", label: "To Coda at the end of this bar", short: "To Coda" },
  { key: "fine", label: "Fine at the end of this bar", short: "Fine" },
];

/** The most passes a list offers; what a file states beyond it is listed as itself. */
export const MOST_PASSES = 8;

/** "1st", "2nd", "3rd", "11th": the pass an ending bar is played on, as its bracket numbers it. */
export function nth(number: number): string {
  const last = number % 100;
  if (last >= 11 && last <= 13) return `${number}th`;
  return `${number}${["th", "st", "nd", "rd"][number % 10] ?? "th"}`;
}

/**
 * The ending numbers a bar is offered: the first three, one past the highest the score
 * already numbers, so a fourth-time bar can be written once there is a third, and whatever
 * the bar itself is in.
 */
export function endingNumbers(score: Score, held: readonly number[]): readonly number[] {
  const written = score.measures.flatMap((measure) => measure.repeat?.endings ?? []);
  const offered = Math.min(MOST_PASSES, Math.max(2, ...written) + 1);
  const numbers = new Set([...Array(offered).keys()].map((at) => at + 1).concat(held));
  return [...numbers].sort((one, other) => one - other);
}

/**
 * The lengths a bar can be shortened to, longest first.
 *
 * A pickup is asked for as a note value and not as a number of ticks: an upbeat of a dotted
 * crotchet is what a musician means, and 10080 is not. Only the values shorter than the
 * metre are offered, because a bar states its own length only in order to be shorter.
 */
export function pickupLengths(
  score: Score,
  index: number,
): readonly { ticks: number; label: string }[] {
  const { time } = resolveAttributes(score, index);
  const metre = measureTicks(time.beats, time.beatType);
  const lengths: { ticks: number; label: string }[] = [];
  for (const duration of DURATIONS) {
    for (const dots of [1, 0]) {
      const ticks = ticksOf(dots === 0 ? plain(duration.type) : withDots(plain(duration.type), 1));
      if (ticks <= 0 || ticks >= metre) continue;
      lengths.push({
        ticks,
        label: dots === 0 ? duration.label : `Dotted ${duration.label.toLowerCase()}`,
      });
    }
  }
  return lengths.sort((a, b) => b.ticks - a.ticks);
}

/** What is written on a bar: its barline, its repeats, its rehearsal mark, its breaks. */
export function barMenu(actions: ScoreActions): Menu {
  const { score, caret } = actions;
  const at = caret?.measureId ?? null;
  const measure = at === null ? null : (score.measures.find((bar) => bar.id === at) ?? null);
  const away = measure === null;

  // Every item in this menu writes to the bar the caret stands in, and is offered greyed
  // out rather than hidden when it stands in none: a menu whose length changes with the
  // caret is a menu nothing can be found in twice.
  const on = (make: (id: MeasureId) => void) => (): void => {
    if (at === null) return;
    make(at);
  };

  return {
    title: "Bar",
    items: [
      ...BARLINES.map((barline) => ({
        label: `Barline: ${barline.label.toLowerCase()}`,
        disabled: away,
        checked: measure?.barline === barline.style,
        run: on((id) => actions.edit(bars.setBarline(score, id, barline.style))),
      })),
      RULE,
      {
        label: "Repeat starts here",
        disabled: away,
        checked: measure?.repeat?.start === true,
        run: on((id) =>
          actions.edit(bars.setRepeatStart(score, id, measure?.repeat?.start !== true)),
        ),
      },
      {
        label: "Repeat ends here",
        disabled: away,
        checked: measure?.repeat?.end === true,
        run: on((id) => actions.edit(bars.setRepeatEnd(score, id, measure?.repeat?.end !== true))),
      },
      ...["First", "Second", "Third"].map((label, at) => ({
        label: `${label}-time bar`,
        disabled: away,
        checked: measure?.repeat?.endings.includes(at + 1) === true,
        run: on((id) =>
          actions.edit(
            bars.setEndings(
              score,
              id,
              measure?.repeat?.endings.includes(at + 1) === true ? [] : [at + 1],
            ),
          ),
        ),
      })),
      RULE,
      ...SIGNS.map((sign) => ({
        label: sign.label,
        disabled: away,
        checked: measure?.jump?.[sign.key] === true,
        run: on((id) =>
          actions.edit(bars.setJump(score, id, { [sign.key]: measure?.jump?.[sign.key] !== true })),
        ),
      })),
      {
        label: "D.C. at the end of this bar",
        disabled: away,
        checked: measure?.jump?.jump === "daCapo",
        run: on((id) =>
          actions.edit(
            bars.setJump(score, id, {
              jump: measure?.jump?.jump === "daCapo" ? null : "daCapo",
            }),
          ),
        ),
      },
      {
        label: "D.S. at the end of this bar",
        disabled: away,
        checked: measure?.jump?.jump === "dalSegno",
        run: on((id) =>
          actions.edit(
            bars.setJump(score, id, {
              jump: measure?.jump?.jump === "dalSegno" ? null : "dalSegno",
            }),
          ),
        ),
      },
      RULE,
      {
        label: "Break the system here",
        disabled: away,
        checked: measure?.breakBefore === "system",
        run: on((id) =>
          actions.edit(
            bars.setBreak(score, id, measure?.breakBefore === "system" ? null : "system"),
          ),
        ),
      },
      {
        label: "Break the page here",
        disabled: away,
        checked: measure?.breakBefore === "page",
        run: on((id) =>
          actions.edit(bars.setBreak(score, id, measure?.breakBefore === "page" ? null : "page")),
        ),
      },
      RULE,
      ...pickupLengths(
        score,
        score.measures.findIndex((bar) => bar.id === at),
      ).map((length) => ({
        label: `Bar lasts a ${length.label.toLowerCase()}`,
        disabled: away,
        checked: measure?.length === length.ticks,
        run: on((id) => actions.edit(bars.setLength(score, id, length.ticks))),
      })),
      {
        label: "Bar lasts its full metre",
        disabled: away,
        checked: measure !== null && measure.length === null,
        run: on((id) => actions.edit(bars.setLength(score, id, null))),
      },
    ],
  };
}
